// Country-weight aggregation for the Node Globe panel. Uses the same
// city hotspot table as landmassTexture.js, tagged with ISO-2 country
// codes so we can roll up totals per country.
//
// [lat, lon, weight, countryCode]
const NODE_CITY_WEIGHTS = [
  // North America
  [40.71,  -74.00, 180, 'US'], [37.77, -122.42, 120, 'US'],
  [34.05, -118.25,  80, 'US'], [41.88,  -87.63,  90, 'US'],
  [47.61, -122.33,  70, 'US'], [33.45, -112.07,  40, 'US'],
  [29.76,  -95.36,  60, 'US'], [25.76,  -80.19,  50, 'US'],
  [39.95,  -75.17,  50, 'US'], [32.72, -117.16,  40, 'US'],
  [38.90,  -77.04,  50, 'US'], [30.27,  -97.74,  60, 'US'],
  [36.17, -115.14,  30, 'US'], [43.65,  -79.38,  80, 'CA'],
  [45.50,  -73.57,  40, 'CA'], [49.28, -123.12,  50, 'CA'],
  // Europe
  [52.52,   13.41, 200, 'DE'], [50.11,    8.68, 110, 'DE'],
  [48.14,   11.58,  70, 'DE'], [53.55,    9.99,  60, 'DE'],
  [51.51,   -0.13, 140, 'GB'], [53.48,   -2.24,  40, 'GB'],
  [48.86,    2.35, 100, 'FR'], [52.37,    4.90, 120, 'NL'],
  [50.85,    4.35,  40, 'BE'], [59.33,   18.07,  70, 'SE'],
  [55.68,   12.57,  50, 'DK'], [60.17,   24.94,  50, 'FI'],
  [59.91,   10.75,  40, 'NO'], [47.37,    8.54,  60, 'CH'],
  [48.21,   16.37,  40, 'AT'], [45.46,    9.19,  40, 'IT'],
  [41.90,   12.50,  30, 'IT'], [40.42,   -3.70,  35, 'ES'],
  [41.38,    2.17,  30, 'ES'], [55.75,   37.62,  90, 'RU'],
  [59.93,   30.33,  40, 'RU'], [50.45,   30.52,  50, 'UA'],
  [52.23,   21.01,  30, 'PL'], [50.08,   14.43,  25, 'CZ'],
  [44.43,   26.10,  20, 'RO'], [37.98,   23.73,  20, 'GR'],
  [41.01,   28.98,  30, 'TR'], [53.34,   -6.26,  25, 'IE'],
  // Asia-Pacific
  [35.69,  139.70, 130, 'JP'], [34.69,  135.50,  30, 'JP'],
  [37.56,  126.99,  90, 'KR'], [ 1.29,  103.85,  60, 'SG'],
  [22.31,  114.17,  50, 'HK'], [25.03,  121.57,  30, 'TW'],
  [31.23,  121.47,  40, 'CN'], [39.90,  116.40,  30, 'CN'],
  [22.54,  114.06,  20, 'CN'], [13.76,  100.50,  25, 'TH'],
  [14.60,  120.98,  15, 'PH'], [-6.21,  106.85,  20, 'ID'],
  [ 3.14,  101.69,  15, 'MY'], [28.61,   77.21,  35, 'IN'],
  [19.07,   72.88,  40, 'IN'], [12.97,   77.59,  40, 'IN'],
  // Oceania
  [-33.87, 151.21,  60, 'AU'], [-37.81, 144.96,  40, 'AU'],
  [-27.47, 153.03,  20, 'AU'], [-31.95, 115.86,  15, 'AU'],
  [-36.85, 174.76,  15, 'NZ'],
  // Latin America
  [-23.55, -46.63,  30, 'BR'], [-22.91, -43.17,  15, 'BR'],
  [-34.61, -58.38,  20, 'AR'], [-33.45, -70.67,  15, 'CL'],
  [ 4.71,  -74.07,  10, 'CO'], [-12.05, -77.04,   8, 'PE'],
  [19.43,  -99.13,  25, 'MX'],
  // Africa + Middle East
  [30.05,   31.24,  15, 'EG'], [33.57,   -7.59,  10, 'MA'],
  [-26.20,  28.04,  20, 'ZA'], [-33.92,  18.42,  15, 'ZA'],
  [ 6.52,    3.38,  10, 'NG'], [-1.29,   36.82,  10, 'KE'],
  [25.20,   55.27,  20, 'AE'], [32.08,   34.78,  20, 'IL'],
];

export const COUNTRY_NAMES = {
  US: 'United States', CA: 'Canada', DE: 'Germany', GB: 'United Kingdom',
  FR: 'France', NL: 'Netherlands', BE: 'Belgium', SE: 'Sweden',
  DK: 'Denmark', FI: 'Finland', NO: 'Norway', CH: 'Switzerland',
  AT: 'Austria', IT: 'Italy', ES: 'Spain', RU: 'Russia',
  UA: 'Ukraine', PL: 'Poland', CZ: 'Czechia', RO: 'Romania',
  GR: 'Greece', TR: 'Turkey', IE: 'Ireland', JP: 'Japan',
  KR: 'South Korea', SG: 'Singapore', HK: 'Hong Kong', TW: 'Taiwan',
  CN: 'China', TH: 'Thailand', PH: 'Philippines', ID: 'Indonesia',
  MY: 'Malaysia', IN: 'India', AU: 'Australia', NZ: 'New Zealand',
  BR: 'Brazil', AR: 'Argentina', CL: 'Chile', CO: 'Colombia',
  PE: 'Peru', MX: 'Mexico', EG: 'Egypt', MA: 'Morocco',
  ZA: 'South Africa', NG: 'Nigeria', KE: 'Kenya', AE: 'UAE',
  IL: 'Israel',
};

export const getCountryNodeTotals = (totalNodes = 0) => {
  const perCountry = new Map();
  let weightSum = 0;
  for (const [, , weight, code] of NODE_CITY_WEIGHTS) {
    if (!code) continue;
    perCountry.set(code, (perCountry.get(code) || 0) + weight);
    weightSum += weight;
  }
  const norm = weightSum || 1;
  return Array.from(perCountry.entries())
    .map(([code, weight]) => ({
      code,
      name: COUNTRY_NAMES[code] || code,
      weight,
      share: weight / norm,
      nodes: totalNodes ? Math.round((weight / norm) * totalNodes) : null,
    }))
    .sort((a, b) => b.weight - a.weight);
};
